import React, { useEffect, useMemo, useState, useRef } from "react";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { isFirstTimeTourCompleted } from "./TourGuideModal";
// Tour after signup disabled — kept for possible replay / rollback:
// import { isTourPendingFirstOpen, requestFirstTimeTourOpen } from "./TourGuideModal";
import { TOUR_FIRST_TIME_DONE_EVENT } from "../../constants/tourSteps";
import { appStorage } from "../../utils/appStorage";
import {
  isPostSignupIntroActive,
  startPostSignupIntroduction,
  POST_SIGNUP_INTRO_FINISHED_EVENT,
} from "../../utils/postSignupOnboarding";

const PROFILE_STEPS = [
  {
    key: "personal_info",
    requiredKeys: ["plan_personal_info", "plan_home_address"],
    label: "Personal Info",
    description: "Name, contact and home address details",
    section: "Personal Info",
    route: "/personal-info",
    optional: false,
  },
];

const OPEN_EVENT = "phonebook:profile-completion-open";
const STEP_EVENT = "phonebook:profile-completion-step";
const REFRESH_EVENT = "phonebook:profile-completion-refresh";
const PENDING_STEP_KEY = "pending_profile_completion_step";

const normalizeFlag = (value) => Number(value) === 1 ? 1 : 0;

const getStoredPendingStep = () => {
  try {
    const raw = appStorage.getItem(PENDING_STEP_KEY);
    if (!raw) return null;
    appStorage.removeItem(PENDING_STEP_KEY);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (error) {
    return null;
  }
};

const setStoredPendingStep = (step) => {
  try {
    if (step) appStorage.setItem(PENDING_STEP_KEY, JSON.stringify(step));
    else appStorage.removeItem(PENDING_STEP_KEY);
  } catch (error) {
    // ignore storage failures
  }
};

const getStepCompletionState = (step, profile = {}) => {
  const requiredKeys = Array.isArray(step.requiredKeys) && step.requiredKeys.length > 0
    ? step.requiredKeys
    : [step.key];
  const missingKeys = requiredKeys.filter((key) => normalizeFlag(profile[key]) !== 1);
  return {
    completed: missingKeys.length === 0 ? 1 : 0,
    missingKeys,
    personalInfoTarget: missingKeys.includes("plan_personal_info")
      ? "basic"
      : missingKeys.includes("plan_home_address")
        ? "address"
        : null,
  };
};

export const isProfileSetupComplete = (profile = {}) =>
  getStepCompletionState(PROFILE_STEPS[0], profile).completed === 1;

const ProfileCompletionModal = ({
  open,
  loading,
  completionPercent,
  steps,
  onContinueSetup,
  onStepClick,
}) => {
  if (!open) return null;

  const activeSteps = Array.isArray(steps) ? steps : PROFILE_STEPS;

  return (
    <div className="fixed inset-0 z-[30000] flex items-center justify-center bg-black/45 p-4 backdrop-blur-[2px]">
      <div className="relative w-full max-w-[520px] overflow-hidden rounded-[30px] bg-white shadow-[0_24px_80px_rgba(35,16,83,0.25)]">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#6C45FF] via-[#8A6CFF] to-[#59C5FF]" />

        <div className="px-5 pb-5 pt-6 sm:px-7 sm:pb-4 sm:pt-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 text-center">
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-[28px] bg-gradient-to-br from-[#F5F0FF] via-[#FFFFFF] to-[#EEF9FF] shadow-inner ring-1 ring-[#ECE6FF]">
                <div className="relative h-16 w-16 rounded-2xl border-4 border-[#8D72FF] bg-white shadow-sm">
                  <div className="absolute left-3 top-3 h-3 w-3 rounded-full bg-[#8D72FF]" />
                  <div className="absolute left-3 top-8 h-2 w-8 rounded-full bg-[#E7E1FF]" />
                  <div className="absolute left-3 top-11 h-2 w-6 rounded-full bg-[#E7E1FF]" />
                  <div className="absolute -right-2 bottom-0 flex h-10 w-10 items-center justify-center rounded-full border-4 border-white bg-[#3AC97A] text-white shadow-lg">
                    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12l4 4 10-10" />
                    </svg>
                  </div>
                </div>
              </div>
              <h2 className="mt-4 text-[22px] font-extrabold tracking-tight text-[#191A23] sm:text-[26px]">
                Welcome to  PhoneBookPlus 👋
              </h2>
              <p className="mx-auto mt-1 max-w-[360px] text-sm leading-6 text-[#6E7280] sm:text-[15px]">
                Let&apos;s complete your profile so friends can recognize you easily.
              </p>

              <div className="mt-3 rounded-2xl bg-[#F7F6FF] px-4 py-3 text-left">
                <div className="flex items-center justify-between gap-3 text-[13px] font-semibold text-[#30313A]">
                  <span>Profile Completion</span>
                  <span>{Math.round(completionPercent)}%</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/90">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#6C45FF] via-[#7A5AF8] to-[#5D7BFF] transition-all duration-500"
                    style={{ width: `${Math.max(8, Math.min(100, completionPercent))}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 space-y-3">
            {activeSteps.map((step, index) => {
              const isComplete = normalizeFlag(step.completed) === 1;
              return (
                <button
                  key={step.key}
                  type="button"
                  disabled={loading}
                  onClick={() => onStepClick(step)}
                  className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition ${isComplete ? "border-[#E8EBF5] bg-[#FBFCFE]" : "border-[#EEE6FF] bg-white hover:border-[#D6C5FF] hover:bg-[#FAF8FF]"}`}
                >
                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${isComplete ? "bg-[#34C77D] text-white" : "bg-[#F1EDFF] text-[#6C45FF]"}`}>
                    {isComplete ? (
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M5 12l4 4 10-10" />
                      </svg>
                    ) : (
                      <span className="text-sm font-bold">{index + 1}</span>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-[15px] font-extrabold text-[#1E1F29] sm:text-base">{step.label}</p>
                      {step.optional && (
                        <span className="rounded-full bg-[#F3F0FF] px-2 py-0.5 text-[11px] font-semibold text-[#6C45FF]">Optional</span>
                      )}
                    </div>
                    <p className="mt-0.5 text-sm text-[#7C8090]">{step.description}</p>
                  </div>

                  <div className="shrink-0 text-sm font-semibold text-[#6C45FF]">
                    {isComplete ? "Edit" : (
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M9 6l6 6-6 6" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={onContinueSetup}
            disabled={loading}
            className="mt-3 flex h-12 w-full items-center justify-center rounded-2xl bg-gradient-to-r from-[#6C45FF] to-[#7E52FF] text-sm font-bold text-white shadow-[0_12px_30px_rgba(108,69,255,0.28)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70"
          >
            Continue Setup
          </button>
        </div>
      </div>
    </div>
  );
};

export function ProfileCompletionGate() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState(PROFILE_STEPS.map((step) => ({ ...step, completed: 1 })));
  const suppressOpenUntilRef = useRef(0);
  const pendingAfterTourRef = useRef(false);
  const tourOpenRequestedRef = useRef(false);

  const visibleSteps = useMemo(() => steps, [steps]);
  const completionPercent = useMemo(() => {
    if (!visibleSteps.length) return 100;
    const completed = visibleSteps.filter((step) => normalizeFlag(step.completed) === 1).length;
    return (completed / visibleSteps.length) * 100;
  }, [visibleSteps]);

  const applyStepSelection = (step) => {
    if (!step) return;
    // persist a pending step so destination page can handle completion lock
    try { setStoredPendingStep(step); } catch (e) {}
    // suppress auto-reopen for a short time after navigating
    try { suppressOpenUntilRef.current = Date.now() + 1500; } catch (e) {}
    if (step.route) navigate(step.route);
    if (step.section) {
      appStorage.setItem("active_section", step.section);
    }
  };

  const refreshProfileState = async (forceOpen = true) => {
    const token = Cookies.get("auth_token");
    // Debug: log token presence to help diagnose why modal may not open
    try {
      // avoid logging actual token value in production; log presence only
      } catch (e) {}
    if (!token) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const profile = data?.data?.[0]?.profile || data?.profile || {};

      const nextSteps = PROFILE_STEPS.map((step) => {
        const completionState = getStepCompletionState(step, profile);
        return {
          ...step,
          completed: completionState.completed,
          missingKeys: completionState.missingKeys,
          personalInfoTarget: completionState.personalInfoTarget || step.personalInfoTarget || null,
        };
      });
      setSteps(nextSteps);

      const hasIncomplete = nextSteps.some((step) => normalizeFlag(step.completed) !== 1);

      // Debug: surface computed values so it's easy to see why modal opens or not
      try {
        } catch (e) {}

      const shouldOpen = Boolean(forceOpen && hasIncomplete && Date.now() > (suppressOpenUntilRef.current || 0));

      // Post-signup: show Introduction first (Skip → profile modal). Hold profile modal while intro is active.
      if (isPostSignupIntroActive()) {
        pendingAfterTourRef.current = true;
        setOpen(false);
        return;
      }

      // After signup / first session: redirect to Introduction instead of opening tour modal
      if (shouldOpen && !isFirstTimeTourCompleted()) {
        pendingAfterTourRef.current = true;
        setOpen(false);

        if (!tourOpenRequestedRef.current) {
          tourOpenRequestedRef.current = true;
          // --- Tour modal disabled for post-signup: open Introduction instead ---
          // requestFirstTimeTourOpen();
          startPostSignupIntroduction(navigate);
        }
        return;
      }

      // Legacy tour-pending path (kept commented — intro replaces tour after signup)
      // if (shouldOpen && isTourPendingFirstOpen() && !isFirstTimeTourCompleted()) {
      //   pendingAfterTourRef.current = true;
      //   setOpen(false);
      //   return;
      // }
      // if (shouldOpen && !isFirstTimeTourCompleted()) {
      //   pendingAfterTourRef.current = true;
      //   setOpen(false);
      //   if (!tourOpenRequestedRef.current) {
      //     tourOpenRequestedRef.current = true;
      //     requestFirstTimeTourOpen();
      //   }
      //   return;
      // }

      pendingAfterTourRef.current = false;
      tourOpenRequestedRef.current = false;
      setOpen(shouldOpen);
    } catch (error) {
      }
  };

  useEffect(() => {
    void refreshProfileState(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const handleRefresh = () => {
      void refreshProfileState(true);
    };

    const handleOpen = () => {
      void refreshProfileState(true);
    };

    const handleTourDone = () => {
      if (!pendingAfterTourRef.current) return;
      void refreshProfileState(true);
    };

    // After post-signup Introduction Skip → open profile completion modal
    const handleIntroFinished = () => {
      pendingAfterTourRef.current = false;
      tourOpenRequestedRef.current = false;
      void refreshProfileState(true);
    };

    window.addEventListener(REFRESH_EVENT, handleRefresh);
    window.addEventListener(OPEN_EVENT, handleOpen);
    window.addEventListener(TOUR_FIRST_TIME_DONE_EVENT, handleTourDone);
    window.addEventListener(POST_SIGNUP_INTRO_FINISHED_EVENT, handleIntroFinished);

    return () => {
      window.removeEventListener(REFRESH_EVENT, handleRefresh);
      window.removeEventListener(OPEN_EVENT, handleOpen);
      window.removeEventListener(TOUR_FIRST_TIME_DONE_EVENT, handleTourDone);
      window.removeEventListener(POST_SIGNUP_INTRO_FINISHED_EVENT, handleIntroFinished);
    };
  }, [location.pathname]);

  const handleStepClick = (step) => {
    const nextStep = PROFILE_STEPS.find((item) => item.key === step.key) || step;
    const withTarget = {
      ...nextStep,
      personalInfoTarget: step.personalInfoTarget || nextStep.personalInfoTarget || "basic",
    };
    applyStepSelection(withTarget);
    try {
      window.dispatchEvent(new CustomEvent(STEP_EVENT, { detail: withTarget }));
    } catch (error) {
      // ignore dispatch failures
    }
    setOpen(false);
  };

  const handleContinueSetup = () => {
    const firstIncomplete = visibleSteps.find((step) => normalizeFlag(step.completed) !== 1) || visibleSteps[0];
    if (firstIncomplete) {
      handleStepClick(firstIncomplete);
      return;
    }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <ProfileCompletionModal
      open={open}
      loading={loading}
      completionPercent={completionPercent}
      steps={visibleSteps}
      onContinueSetup={handleContinueSetup}
      onStepClick={handleStepClick}
    />
  );
}

export default ProfileCompletionModal;